import React, { useState } from "react";
import { Formik, Form } from "formik";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";

import routes from "constants/routes";
import { useAuthentication } from "store/backend/login";
import { ReactComponent as Logo } from "shared/Icons/logo.svg";
import Loader from "shared/Loader/Loader";
import { useSnackbarContext } from "widgets/SnackBar/SnackBar";

import styles from "./Login.module.scss";

const loginSchema = Yup.object({
  mobile: Yup.string()
    .required("Mobile number is required")
    .matches(/^[0-9]{10}$/, "Mobile number must be exactly 10 digits"),
  password: Yup.string()
    .required("Password is required")
    .min(1, "Password must contain at least 8 character"),
});

const initialValues = {
  mobile: "",
  password: "",
};

const handleSubmit = (
  values,
  login,
  navigate,
  setSubmitting,
  openSnackbar,
  setApiError
) => {
  setApiError("");

  login(
    {
      mobile: values.mobile,
      password: values.password,
    },
    {
      onSuccess: () => {
        openSnackbar("Login Successful", "success");
        navigate(routes.coupons.list);
      },
      onError: (response) => {
        setApiError(
          response?.message || "Invalid username or password. Please try again."
        );
      },
      onSettled: () => {
        setSubmitting(false);
      },
    }
  );
};

const LoginForm = () => {
  const navigate = useNavigate();
  const { openSnackbar } = useSnackbarContext();
  const { mutate: login, isPending } = useAuthentication();
  const [showPassword, setShowPassword] = useState(false);
  const [apiError, setApiError] = useState("");

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={loginSchema}
      validateOnMount
      onSubmit={(values, { setSubmitting }) =>
        handleSubmit(
          values,
          login,
          navigate,
          setSubmitting,
          openSnackbar,
          setApiError
        )
      }
    >
      {(formikProps) => {
        const {
          isValid,
          values,
          errors,
          isSubmitting,
          setFieldValue,
          handleBlur,
        } = formikProps;

        const clearApiError = () => {
          if (apiError) {
            setApiError("");
          }
        };

        return (
          <Form>
            <div className={styles.loginContentContainer}>
              {apiError ? (
                <div className={styles.apiErrorBanner} role="alert">
                  {apiError}
                </div>
              ) : null}

              <div className={styles.inputContainer}>
                <label className={styles.fieldLabel} htmlFor="mobile">
                  Mobile Number
                </label>
                <div className={styles.inputWrapper}>
                  <PhoneOutlinedIcon className={styles.inputIcon} />
                  <input
                    id="mobile"
                    className={styles.styledInput}
                    type="tel"
                    placeholder="Enter your mobile number"
                    value={values.mobile}
                    onBlur={handleBlur}
                    onChange={(event) => {
                      clearApiError();
                      setFieldValue("mobile", event.target.value);
                    }}
                  />
                </div>
                {errors.mobile ? (
                  <p className={styles.errorText}>{errors.mobile}</p>
                ) : null}
              </div>

              <div className={styles.inputContainer}>
                <label className={styles.fieldLabel} htmlFor="password">
                  Password
                </label>
                <div className={styles.inputWrapper}>
                  <LockOutlinedIcon className={styles.inputIcon} />
                  <input
                    id="password"
                    className={styles.styledInput}
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={values.password}
                    onBlur={handleBlur}
                    onChange={(event) => {
                      clearApiError();
                      setFieldValue("password", event.target.value);
                    }}
                  />
                  <button
                    type="button"
                    className={styles.passwordToggle}
                    onClick={() => setShowPassword((prev) => !prev)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <VisibilityOffOutlinedIcon fontSize="small" />
                    ) : (
                      <VisibilityOutlinedIcon fontSize="small" />
                    )}
                  </button>
                </div>
                {errors.password ? (
                  <p className={styles.errorText}>{errors.password}</p>
                ) : null}
              </div>

              <div className={styles.btnContainer}>
                <button
                  type="submit"
                  className={styles.signInButton}
                  disabled={!isValid || isSubmitting}
                >
                  {isPending ? <Loader /> : "Sign In"}
                </button>
              </div>

              <p className={styles.supportText}>
                Don&apos;t have an account? Contact support team.
              </p>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

const Login = () => {
  return (
    <div className={styles.rootContainer}>
      <div className={styles.brandPanel}>
        <div className={styles.brandContent}>
          <div className={styles.brandLogoWrap}>
            <Logo className={styles.brandLogo} aria-label="Infinity Learn" />
          </div>

          <h1 className={styles.welcomeTitle}>
            Welcome to <span className={styles.brandHighlight}>Minerva</span>
          </h1>
          <p className={styles.welcomeSubtitle}>
            Package, Coupon &amp; Upload Management Platform
          </p>

          <div className={styles.featureList}>
            <div className={styles.featureItem}>
              <Inventory2OutlinedIcon
                className={`${styles.featureIcon} ${styles.featureIconPackages}`}
              />
              <div className={styles.featureText}>
                <span className={styles.featureTitle}>Packages</span>
                <span className={styles.featureDesc}>
                  Create, manage and track all your packages.
                </span>
              </div>
            </div>
            <div className={styles.featureItem}>
              <LocalOfferOutlinedIcon
                className={`${styles.featureIcon} ${styles.featureIconCoupons}`}
              />
              <div className={styles.featureText}>
                <span className={styles.featureTitle}>Coupons</span>
                <span className={styles.featureDesc}>
                  Create, update and track coupons.
                </span>
              </div>
            </div>
            <div className={styles.featureItem}>
              <CloudUploadOutlinedIcon
                className={`${styles.featureIcon} ${styles.featureIconUploads}`}
              />
              <div className={styles.featureText}>
                <span className={styles.featureTitle}>Uploads</span>
                <span className={styles.featureDesc}>
                  Bulk upload packages and coupons.
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.formPanel}>
        <div className={styles.loginCard}>
          <h2 className={styles.loginHeading}>Sign in to your account</h2>
          <p className={styles.loginSubheading}>
            Enter your credentials to access Minerva
          </p>
          <LoginForm />
        </div>
      </div>
    </div>
  );
};

export default Login;
